<template>
  <div class="fcp-root">
    <div class="fcp-wrapper">
      <div class="fcp-card">
        <div class="text-center mb-4">
          <img :src="$brand.logo" :alt="$brand.name" class="fcp-logo" />
          <div class="fcp-welcome mt-3">Welcome to {{ $brand.name }} 👋</div>
          <p class="fcp-sub">Set a new password before you continue. Your temporary password will no longer work after this.</p>
        </div>

        <div class="mb-3">
          <label class="auth-label">New password <span class="text-danger">*</span></label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-lock-outline"></i></span>
            <input :type="show ? 'text' : 'password'" v-model="form.newPw" class="form-control" placeholder="At least 8 characters" autocomplete="new-password" />
            <button type="button" class="input-group-text" @click="show = !show"><i :class="show ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i></button>
          </div>
        </div>

        <div class="mb-3">
          <label class="auth-label">Confirm new password <span class="text-danger">*</span></label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-lock-check"></i></span>
            <input :type="show ? 'text' : 'password'" v-model="form.confirmPw" class="form-control" placeholder="Type it again" autocomplete="new-password" @keyup.enter="submit" />
          </div>
        </div>

        <div class="fcp-checklist mb-3">
          <div :class="ruleCls(form.newPw.length >= 8)"><i :class="ruleIcon(form.newPw.length >= 8)"></i> At least 8 characters</div>
          <div :class="ruleCls(form.newPw && form.confirmPw && form.newPw === form.confirmPw)"><i :class="ruleIcon(form.newPw && form.confirmPw && form.newPw === form.confirmPw)"></i> Both fields match</div>
          <div :class="ruleCls(/[0-9]/.test(form.newPw))"><i :class="ruleIcon(/[0-9]/.test(form.newPw))"></i> Contains a number (recommended)</div>
          <div :class="ruleCls(/[^A-Za-z0-9]/.test(form.newPw))"><i :class="ruleIcon(/[^A-Za-z0-9]/.test(form.newPw))"></i> Contains a symbol (recommended)</div>
        </div>

        <div v-if="errorMsg" class="alert alert-danger py-2 px-3"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

        <button class="btn btn-primary w-100" @click="submit" :disabled="!canSubmit || submitting">
          <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Saving…</span>
          <span v-else><i class="mdi mdi-content-save me-1"></i> Set password and continue</span>
        </button>

        <div class="text-center mt-3">
          <a href="javascript:void(0)" class="fcp-link" @click="signOut">Sign out and return to login</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, hms } from "@/api";

export default {
  name: "ForceChangePassword",
  data() {
    return {
      show: false,
      submitting: false,
      errorMsg: "",
      form: { newPw: "", confirmPw: "" }
    };
  },
  computed: {
    canSubmit() {
      return this.form.newPw.length >= 8 && this.form.newPw === this.form.confirmPw;
    }
  },
  mounted() {
    if (!localStorage.getItem("authToken")) {
      this.$router.replace("/login");
    }
  },
  methods: {
    ruleCls(ok) { return "fcp-rule " + (ok ? "ok" : ""); },
    ruleIcon(ok) { return ok ? "mdi mdi-check-circle text-success me-1" : "mdi mdi-circle-outline text-muted me-1"; },
    signOut() {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userProfile");
      sessionStorage.clear();
      this.$router.push("/login");
    },
    async submit() {
      if (!this.canSubmit) return;
      this.errorMsg = "";
      this.submitting = true;
      try {
        const r = await executeJson({ function: "forceChangePassword", new_password: this.form.newPw }, hms);
        if (r.data && r.data.success) {
          // Route them to their scope home based on the userProfile we cached at login
          let profile = {};
          try { profile = JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch (e) { /* ignore */ }
          const scope = profile.user_scope || "hospital";
          if (scope === "platform")          this.$router.push("/platform");
          else if (scope === "organization") this.$router.push("/org");
          else                                this.$router.push("/dashboard");
        } else {
          this.errorMsg = (r.data && r.data.message) || "Could not set password. Please try again.";
        }
      } catch (e) {
        this.errorMsg = (e.response && e.response.data && e.response.data.message) || "Connection error. Please try again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.fcp-root { background: var(--hms-bg2); min-height: 100vh; }
.fcp-wrapper { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; }
.fcp-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 36px 32px; width: 100%; max-width: 460px; box-shadow: 0 12px 40px rgba(0,0,0,.06); }
.fcp-logo { height: 52px; }
.fcp-welcome { font-size: 18px; font-weight: 700; color: var(--hms-h); }
.fcp-sub { color: var(--hms-m); font-size: 13px; line-height: 1.5; margin-top: 6px; }
.auth-label { font-size: 12px; font-weight: 600; color: var(--hms-h); margin-bottom: 4px; display: block; }

.fcp-checklist { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; }
.fcp-rule { font-size: 12px; color: var(--hms-s); padding: 2px 0; }
.fcp-rule.ok { color: var(--hms-h); }
.fcp-link { color: var(--hms-primary); font-size: 12px; text-decoration: none; }
.fcp-link:hover { text-decoration: underline; }
</style>
